import clsx from "clsx";
import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import { useBoolean, useCopyToClipboard } from "usehooks-ts";

import { AddUserModal } from "../AddUserModal";

import { AdminUserOptionsMenu } from "./AdminUserOptionsMenu";
import { OrgRoleIconWithTooltip } from "./UserRoleIcons";

import { InviteStatus } from "api/api-data-types";
import { EditOrgUserModal } from "components/modals/EditOrgUserModal";
import type { OrgRoleType, OrgUserType, UserType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TR } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { ArrowDownIcon } from "mds/icons";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { Tooltip } from "mds/components/Tooltip";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  orgUserSelectors,
  sectionSelectors,
  selectCurrentOrg,
  selectCurrentUserId,
  selectCurrentUserIsMPAdmin,
  selectEnabledSectionUsersByRole,
  selectIsEditModeEnabled,
  userSelectors,
} from "store/selectors";
import { selectUserInviteStatuses } from "store/selectors/computed";
import { sortByFirstName } from "store/selectors/sorting";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher, toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateOrgUsersCSV } from "utils/reporting";
import { getSectionResourceRole } from "utils/section";
import { useFetchUserInviteStatuses } from "utils/user-invite-status";
import { createImpersonationUrl, getUserFullName } from "utils/user-utils";

export type MaxRoleType = "super_admin" | "org_admin" | "section_admin" | "instructor" | "student";

type AdminUserEntryType = OrgUserType &
  UserType & {
    org_user_id: string;
    tooltipSectionRoleTable: ReactNode;
    invite_status: InviteStatus;
    max_role: MaxRoleType;
  };

const ORG_ROLE_SORT_ORDER: Record<OrgRoleType, number> = {
  admin: 0,
  other: 1,
  none: 2,
  recording_agent: 3, // filtered out above; here for exhaustiveness
};

const MAX_ROLE_SORT_ORDER: Record<MaxRoleType, number> = {
  super_admin: 0,
  org_admin: 1,
  instructor: 2,
  section_admin: 3,
  student: 4,
};

function getMaxRole(
  orgUser: OrgUserType,
  user: UserType,
  sectionUsersForUser: { role: string }[],
): MaxRoleType {
  if (user.is_superuser) return "super_admin";
  if (orgUser.role === "admin") return "org_admin";
  const hasInstructor = sectionUsersForUser.some((cu) => cu.role === "instructor");
  if (hasInstructor) return "instructor";
  const hasSectionAdmin = sectionUsersForUser.some((cu) => cu.role === "section_admin");
  if (hasSectionAdmin) return "section_admin";
  return "student";
}

type SortField = "name" | "email" | "max_role" | "external_id" | "invite_status";

interface AdminUsersListProps {
  onShowBulkImportModal: () => void;
}

export const AdminUsersList: FC<AdminUsersListProps> = ({ onShowBulkImportModal }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const allUsers = useListSelector(userSelectors.selectAll);
  const orgUsers = useAppSelector(orgUserSelectors.selectAll);
  const userInviteStatuses = useAppSelector(selectUserInviteStatuses);
  const sectionUsers = useListSelector(selectEnabledSectionUsersByRole);
  const sections = useListSelector(sectionSelectors.selectAll);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const { canAuthorOrg, canSeeAllOrgUsers } = useCapabilities();
  const editModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const isMpAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const user_id = useAppSelector(selectCurrentUserId);
  const canToggleDisabled = canAuthorOrg;
  const canChangeOrgRole = canAuthorOrg;
  const canImpersonate = isMpAdmin;
  const isCurrentUserInOrg = orgUsers.some((ou) => ou.user_id === user_id);
  const canAddThemselvesToOrg = isMpAdmin && !isCurrentUserInOrg;
  const isSmallScreen = !useIsMdOrLarger();

  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    storeApi.section_users.list({});
  }, []);

  useFetchUserInviteStatuses();

  // OrgUsers and SectionUsers are both links to the User model, and to Org and Section separately,
  // and both have a "role" string attached.
  // In this view we'd like to show a list of all users that have an OrgUser, their role, and the
  // amount of SectionUsers that they have.

  const userList: AdminUserEntryType[] = orgUsers
    .flatMap((orgUser) => {
      const user = allUsers.find((u) => u.id === orgUser.user_id);
      if (!user || orgUser.role === "recording_agent") {
        return [];
      }
      if (!canSeeAllOrgUsers && user.is_superuser) {
        return [];
      }

      const sectionUsersForUser = sectionUsers.filter((cu) => cu.user_id === orgUser.user_id);

      const tooltipSectionRoleTable = sectionUsersForUser.map((cu) => {
        const section = sections.find((s) => s.id === cu.section_id);
        const role = getSectionResourceRole(cu.role);

        return (
          <div key={cu.id}>
            <strong>{section?.title}</strong>: {t(`roles.section_${role}`)}
          </div>
        );
      });

      const max_role = getMaxRole(orgUser, user, sectionUsersForUser);

      return [
        {
          ...orgUser,
          ...user,
          org_user_id: orgUser.id,
          tooltipSectionRoleTable,
          invite_status: userInviteStatuses[user.id] || InviteStatus.NOT_SENT,
          max_role,
        },
      ];
    })
    .toSorted((a, b) => {
      const roleDiff = ORG_ROLE_SORT_ORDER[a.role] - ORG_ROLE_SORT_ORDER[b.role];
      return roleDiff !== 0 ? roleDiff : sortByFirstName(a, b);
    });

  const canSeeExternalUserIds = canAuthorOrg && userList.some((u) => u.external_user_id);

  const getOrgUserSortValue = useCallback(
    (entry: AdminUserEntryType, field: SortField): number | string | null => {
      switch (field) {
        case "name":
          return `${(entry.first_name ?? "").toLowerCase()} ${(entry.last_name ?? "").toLowerCase()}`.trim();
        case "email":
          return entry.email ?? "";
        case "max_role":
          return MAX_ROLE_SORT_ORDER[entry.max_role];
        case "external_id":
          return entry.external_user_id ?? "";
        case "invite_status":
          return entry.invite_status;
        default:
          return null;
      }
    },
    [],
  );

  const onJoinAsAdmin = () => {
    if (!user_id) {
      return;
    }
    storeApi.org_users
      .create({
        user_id,
        role: "admin",
      })
      .catch(toastLocalizedOperationCatcher("join_as_admin"));
  };

  const onChangeRole = (newRole: "admin" | "other", user: { org_user_id: string }) => {
    storeApi.org_users
      .partial_update({
        id: user.org_user_id,
        role: newRole,
      })
      .catch(toastLocalizedOperationCatcher("update_user_role"));
  };

  const onImpersonate = (user: { id: string }) => {
    createImpersonationUrl(user.id).then(copyToClipboard);
  };

  const onToggleDisabled = (user: { org_user_id: string; is_disabled: boolean }) => {
    storeApi.org_users.partial_update({
      id: user.org_user_id,
      is_disabled: !user.is_disabled,
    });
  };

  const onDownloadCSV = () => {
    if (!currentOrg) {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
      return;
    }
    if (userList.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }
    try {
      const { csvData, filename } = generateOrgUsersCSV(
        userList,
        currentOrg.name,
        canSeeExternalUserIds,
      );
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div className="h-full overflow-y-auto pb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="mb-0 hidden md:block">{t("user_options_menu.org_sidebar_title")}</h3>
        <div className={clsx("flex gap-2", isSmallScreen && "w-full")}>
          {canAddThemselvesToOrg && (
            <LegacyButton className="flex items-center gap-2" size="xs" onClick={onJoinAsAdmin}>
              {/* Do NOT localize this as it's mp admin only */}
              <span>Join this org as an admin</span>
              <Tooltip text="After joining, you can choose your role in the list below." />
            </LegacyButton>
          )}

          <Button
            aria-label={t("admin.add_user.add_user")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Add new"
            kind="white"
            size="xs"
            onClick={() => setShowAddUserModal(true)}
          >
            {t("admin.add_user.add_user")}
          </Button>

          <Button
            aria-label={t("home.settings.bulk_import_modal.title")}
            className="flex-1 md:flex-none"
            disabled={!editModeEnabled}
            eventName="Org Users - Bulk enrollment"
            kind="white"
            size="xs"
            onClick={onShowBulkImportModal}
          >
            {t("home.settings.bulk_import_modal.title")}
          </Button>

          <Button
            aria-label={t("user_options_menu.download_csv")}
            className="flex-1 md:flex-none"
            disabled={userList.length === 0}
            eventName="Org Users - Download CSV"
            kind="white"
            size="xs"
            onClick={onDownloadCSV}
          >
            <IconText iconStart={<ArrowDownIcon />} text={t("user_options_menu.download_csv")} />
          </Button>
        </div>
      </div>

      <ScrollableTableContainer className="mb-0">
        <SortableTableWrapper<AdminUserEntryType, SortField>
          data={userList}
          getSortValue={getOrgUserSortValue}
        >
          <SortableTable>
            <thead>
              <tr>
                <SortableHeader field="name" label={t("user_options_menu.table_header_name")} />
                <SortableHeader field="email" label={t("user_options_menu.table_header_email")} />
                <SortableHeader
                  field="max_role"
                  label={t("user_options_menu.table_header_max_role")}
                />
                {canSeeExternalUserIds && (
                  <SortableHeader
                    field="external_id"
                    label={t("user_options_menu.table_header_external_id")}
                  />
                )}
                <SortableHeader
                  field="invite_status"
                  label={t("user_options_menu.table_header_invite_status")}
                />
              </tr>
            </thead>

            <SortableBody<AdminUserEntryType>>
              {(sortedData) =>
                sortedData.map((user) => (
                  <AdminUserEntry
                    key={user.id}
                    showExternalId={canSeeExternalUserIds}
                    user={user}
                    onChangeRole={canChangeOrgRole ? onChangeRole : undefined}
                    onImpersonate={canImpersonate ? onImpersonate : undefined}
                    onToggleDisabled={canToggleDisabled ? onToggleDisabled : undefined}
                  />
                ))
              }
            </SortableBody>
          </SortableTable>
        </SortableTableWrapper>
      </ScrollableTableContainer>
      {showAddUserModal && <AddUserModal onClose={() => setShowAddUserModal(false)} />}
    </div>
  );
};

type AdminUserEntryProps = {
  user: AdminUserEntryType;
  onChangeRole?: (newRole: OrgRoleType, user: { org_user_id: string }) => void;
  onImpersonate?: (user: { id: string }) => void;
  onToggleDisabled?: (user: { org_user_id: string; is_disabled: boolean }) => void;
  showExternalId?: boolean;
};

export const AdminUserEntry: FC<AdminUserEntryProps> = ({
  user,
  onChangeRole,
  onImpersonate,
  onToggleDisabled,
  showExternalId = false,
}) => {
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean(false);
  const hasActions = Boolean(onChangeRole || onImpersonate || onToggleDisabled);
  return (
    <TR className="items-center justify-between">
      <TD
        className={clsx("flex items-center gap-1", user.is_disabled && "italic text-black-tint-70")}
      >
        <OrgRoleIconWithTooltip
          extraContent={user.tooltipSectionRoleTable}
          role={user.is_superuser ? "superuser" : user.role}
        />

        <span className="max-w-m mb-1 truncate" title={getUserFullName(user)}>
          {getUserFullName(user)}
        </span>
      </TD>

      <TD className="body-xs relative">{user.email}</TD>

      <TD className="body-xs relative">{t(`user_options_menu.max_role.${user.max_role}`)}</TD>

      {showExternalId && <TD className="body-xs relative">{user.external_user_id || "-"}</TD>}

      <TD className="body-xs relative">
        <span>{t(`invite_status.${user.invite_status}`)}</span>
        {hasActions && (
          <div className="absolute right-0 top-0">
            <AdminUserOptionsMenu
              currentRole={user.is_superuser ? "superuser" : user.role}
              user={user}
              onChangeRole={onChangeRole ? (newRole) => onChangeRole(newRole, user) : undefined}
              onEdit={openModal}
              onImpersonate={onImpersonate ? () => onImpersonate(user) : undefined}
              onToggleDisabled={onToggleDisabled ? () => onToggleDisabled(user) : undefined}
            />
          </div>
        )}

        <EditOrgUserModal open={isModalOpen} user={user} onClose={closeModal} />
      </TD>
    </TR>
  );
};
